import React, { useEffect } from 'react';

const NotificationModal = ({ 
    isOpen, 
    onClose, 
    onConfirm, // Si está presente, se convierte en modal de confirmación
    type = 'success', 
    message, 
    title, // Solo para modo confirmación
    confirmText = 'Confirmar', 
    cancelText = 'Cancelar',
    autoCloseDelay = 3000 // Solo para modo notificación
}) => {
    const isConfirmMode = !!onConfirm;

    // Efecto para auto-cerrar en modo notificación
    useEffect(() => {
        if (isOpen && !isConfirmMode && autoCloseDelay > 0) {
            const timer = setTimeout(() => {
                onClose();
            }, autoCloseDelay);

            return () => clearTimeout(timer);
        }
    }, [isOpen, autoCloseDelay, onClose, isConfirmMode]);

    // Efecto para bloquear scroll en modo confirmación
    useEffect(() => {
        if (isOpen && isConfirmMode) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, isConfirmMode]);

    // Efecto para cerrar con ESC en modo confirmación
    useEffect(() => {
        if (!isConfirmMode) return;

        const handleEscape = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose, isConfirmMode]);

    if (!isOpen) return null;

    // Modo confirmación (modal centrado)
    if (isConfirmMode) {
        const iconColor = type === 'warning' ? 'text-amber-500' : type === 'danger' ? 'text-red-500' : 'text-blue-500';
        const confirmButtonColor = type === 'warning' ? 'bg-amber-600 hover:bg-amber-700' : type === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700';

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={onClose}>
                <div className="bg-white rounded-lg shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
                    <div className="p-6">
                        {/* Icono y título */}
                        <div className="flex items-start mb-4">
                            <div className={`flex-shrink-0 ${iconColor} mr-3`}>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div className="flex-1">
                                {title && (
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        {title}
                                    </h3>
                                )}
                                {message && (
                                    <p className="text-sm text-gray-600">
                                        {message}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Botones */}
                        <div className="flex flex-col sm:flex-row gap-3 justify-end mt-6">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition font-medium min-h-[44px] text-sm sm:text-base"
                            >
                                {cancelText}
                            </button>
                            <button
                                onClick={async () => {
                                    if (onConfirm) {
                                        await onConfirm();
                                    }
                                    onClose();
                                }}
                                className={`px-4 py-2 text-white rounded-lg transition font-medium min-h-[44px] text-sm sm:text-base ${confirmButtonColor}`}
                            >
                                {confirmText}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Modo notificación (esquina inferior derecha)
    const isSuccess = type === 'success';
    const bgColor = isSuccess ? 'bg-green-50' : 'bg-red-50';
    const borderColor = isSuccess ? 'border-green-200' : 'border-red-200';
    const textColor = isSuccess ? 'text-green-800' : 'text-red-800';
    const iconColor = isSuccess ? 'text-green-500' : 'text-red-500';

    return (
        <div className="fixed bottom-4 right-4 z-50 max-w-md w-full">
            <div className={`bg-white rounded-lg shadow-xl border-2 ${borderColor} ${bgColor} p-4 transform transition-all duration-300 ease-in-out`}>
                <div className="flex items-start">
                    {/* Icono */}
                    <div className={`flex-shrink-0 ${iconColor}`}>
                        {isSuccess ? (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        )}
                    </div>

                    {/* Mensaje */}
                    <div className="ml-3 flex-1">
                        <p className={`text-sm font-medium ${textColor}`}>
                            {message}
                        </p>
                    </div>

                    {/* Botón cerrar */}
                    <button
                        onClick={onClose}
                        className={`ml-3 flex-shrink-0 ${textColor} hover:opacity-75 transition-opacity`}
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NotificationModal;
